import { useState } from "react";

import Box from "@/components/Box";
import {
  CMP_FAP_EXPERT_APPLICATION_URL,
  CMP_PATIENTS_CALLBACK_REQUESTS_URL,
  CMP_PATIENTS_MANAGE_PATIENTS_URL,
  CMP_SUPPORT_URL
} from "@/core/urls";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import useUserProfile from "@/hooks/useUserProfile";
import CalendarIcon from "@/images/CalendarIcon";
import InvitePatientIcon from "@/images/InvitePatientIcon";
import PatientIcon from "@/images/PatientIcon";
import PhoneRingingIcon from "@/images/PhoneRingingIcon";
import { gql, useQuery } from "@apollo/client";

import DashboardPanel from "../DashboardPanel";
import DashboardPanelSections from "./DashboardPanelSections";
import InvitePatientModal from "./InvitePatientModal";
import {
  cmpHomeDashboardPanelPadding,
  cmpHomeDashboardPanelTitleProps,
  cmpHomeDashboardSectionLeadingBg
} from "./dashboardLayout";

const CALLBACK_REQUESTS_COUNT_QUERY = gql`
  query CallbackRequestsCountForDashboard {
    contactExpertsUnrespondedCount
  }
`;

function CallbackRequestsCountBadge({ count }) {
  return (
    <Box
      width={28}
      height={28}
      minWidth={28}
      borderRadius="50%"
      bg={cmpHomeDashboardSectionLeadingBg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontFamily="gilroyBold"
      fontSize={12}
      lineHeight="12px"
      flexShrink={0}
    >
      {count}
    </Box>
  );
}

export default function PatientsPanel() {
  const { resolvedUrls } = useCmpRelatedUrl();
  const { practitionerProfile } = useUserProfile();
  const [showInvitePatientModal, setShowInvitePatientModal] = useState(false);
  const isApprovedExpert = practitionerProfile?.isApprovedToBeExpert;

  const { data: callbackRequestsCountData } = useQuery(CALLBACK_REQUESTS_COUNT_QUERY, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    skip: !isApprovedExpert
  });

  const callbackRequestsCount = callbackRequestsCountData?.contactExpertsUnrespondedCount ?? 0;

  const sections = [
    {
      title: "View Patients",
      description: "Add, View, Edit Patients",
      to: resolvedUrls[CMP_PATIENTS_MANAGE_PATIENTS_URL],
      startIcon: <PatientIcon size={40} withCircle={false} color="#1A1A1A" />,
      startIconGap: 16
    },
    {
      title: "Invite Patient",
      description: "Send an Invitation to Your Patient",
      onClick: () => setShowInvitePatientModal(true),
      leadingPlaceholderContent: <InvitePatientIcon size={20} />
    },
    {
      title: "Patient Callback Request",
      description: isApprovedExpert
        ? "Requests to Connect via Find a Practitioner"
        : "You Are Not Yet Featured as an Expert, Apply Now",
      ...(isApprovedExpert
        ? {
            to: resolvedUrls[CMP_PATIENTS_CALLBACK_REQUESTS_URL],
            titleLeading: <CallbackRequestsCountBadge count={callbackRequestsCount} />
          }
        : { href: CMP_FAP_EXPERT_APPLICATION_URL }),
      leadingPlaceholderContent: <PhoneRingingIcon size={17.5} withCircle={false} />
    },
    {
      title: "Book a Support Call",
      description: "Schedule a Call with Our Team",
      to: resolvedUrls[CMP_SUPPORT_URL],
      leadingPlaceholderContent: <CalendarIcon size={24} fill="#1A1A1A" />
    }
  ];

  return (
    <>
      <DashboardPanel
        title="Patients"
        titleProps={cmpHomeDashboardPanelTitleProps}
        padding={cmpHomeDashboardPanelPadding}
      >
        <DashboardPanelSections
          sections={sections}
          sectionLeadingPlaceholder={{ size: 40, gap: 16, borderRadius: 12 }}
        />
      </DashboardPanel>
      <InvitePatientModal
        show={showInvitePatientModal}
        close={() => setShowInvitePatientModal(false)}
      />
    </>
  );
}
